import { FC, useEffect, useState } from 'react';
import { TextField, Select, MenuItem, FormControl, InputLabel, SelectChangeEvent, Button, Grid, Typography, SvgIcon } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { client, UomType, UomTypeFilterOptions, Instance } from '../../../app/services/api/orderManagementClient';
import CustomPagination from '../../../components/forms/Pagination';
import { Section } from '../../../components/ui/Section';
import UomTypesList from './UomTypesList';

type Props = {
  onCreateNew: () => void;
};

const UomTypesTab: FC<Props> = ({ onCreateNew }) => {
  const { t } = useTranslation('uomTypes');
  const [list, setList] = useState<UomType[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [instances, setInstances] = useState<Instance[]>([]);
  const pageSize = 20;

  const fetchInstances = async () => {
    try {
      const result = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      if (result.success && result.data) {
        setInstances(result.data.items);
      }
    } catch (error) {
      console.error('Error fetching instances:', error);
    }
  };

  const fetchUomTypes = async () => {
    setIsLoading(true);
    try {
      const options: UomTypeFilterOptions = {
        companyId: selectedInstance || undefined,
        searchTerm: searchTerm || undefined,
        pageNumber: currentPage,
        pageSize: pageSize,
        sort: '',
        direction: 'asc',
      };
      const result = await client.getUomTypes(options); 
      if (result.success && result.data) {
        setList(result.data.items);
        setTotalCount(result.data.totalCount);
      } else {
        setList([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching UOM types:', error);
      setList([]);
      setTotalCount(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInstances();
  }, []);

  useEffect(() => {
    fetchUomTypes();
  }, [currentPage, searchTerm, selectedInstance]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
  };

  const handleSearchKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      onSearch();
    }
  };

  const onSearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(0);
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    setSelectedInstance(event.target.value);
    setCurrentPage(0);
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchTerm('');
    setSelectedInstance('');
    setCurrentPage(0);
  };

  return (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          label={t('search')}
          placeholder={t('searchPlaceholder')}
          value={searchInput}
          onChange={handleSearchInputChange}
          onKeyPress={handleSearchKeyPress}
          fullWidth
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={handleClear}
          variant='secondary'
          size='lg'
        >
          <Typography>{t('clear')}</Typography>
        </Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          onClick={onSearch}
          size='lg'
          startIcon={<SvgIcon><Search /></SvgIcon>}
        >
          <Typography>{t('search')}</Typography>
        </Button>
      </Grid>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <FormControl fullWidth>
          <InputLabel shrink>
            {t('filterByInstance')}
          </InputLabel>
          <Select
            value={selectedInstance}
            onChange={handleInstanceChange}
            label={t('filterByInstance')}
            displayEmpty
            notched
          >
            <MenuItem value="">
              {t('allInstances')}
            </MenuItem>
            {instances.map((instance) => (
              <MenuItem 
                key={instance.id} 
                value={instance.id.toString()}
              >
                {instance.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item
        xs={12}>
        <Section>
          <UomTypesList
            list={list}
            isLoading={isLoading}
            totalCount={totalCount}
            onClear={handleClear}
            onCreateNew={onCreateNew}
          />
          {totalCount > pageSize && (
            <CustomPagination
              page={currentPage}
              setPage={handlePageChange}
              maxPages={Math.ceil(totalCount / pageSize)}
              breakpointForChangeDisplay={120000}
              useNoDataMessage={false}
            />
          )}
        </Section>
      </Grid>
    </Grid>
  );
};

export default UomTypesTab;
